/**
 * Data Transformers
 * 
 * Type-safe transformers that convert raw Supabase data to application types.
 * These handle the Json -> proper type conversions and null coalescing.
 * 
 * RULE: All Supabase queries should pass through these transformers before
 * reaching components. Components should never deal with Json or null coalescing.
 */

import type { Json } from './types';
import type { 
  PublishedStorybook as RawStorybook,
  PublishedFlashcardBook as RawFlashcardBook,
  StorybookPage as RawStorybookPage,
  FlashcardPage as RawFlashcardPage,
  PortalSettings as RawPortalSettings,
  ReadingRecording as RawReadingRecording,
  Profile as RawProfile,
  TurnColorWord as RawTurnColorWord,
  TurnColorMapping as RawTurnColorMapping,
} from './index';

// =============================================================================
// APPLICATION TYPES (What components use)
// =============================================================================

export interface Storybook {
  id: string;
  title: string;
  slug: string;
  description: string;
  coverImageUrl: string;
  introAudioUrl: string;
  founderImageUrl: string;
  turnCodes: Record<string, string>;
  colorMappings: ColorMapping[];
  enabledColors: string[];
  phase: string;
  ageRangeMin: number;
  ageRangeMax: number;
  isPublic: boolean;
  isPublished: boolean;
  createdAt: string;
}

export interface StorybookPage {
  id: string;
  storybookId: string;
  pageNumber: number;
  text: string;
  imageUrl: string;
  sceneDescription: string;
  audioUrl: string;
  audioTimestamps: AudioTimestamp[];
}

export interface AudioTimestamp {
  word: string;
  start: number;
  end: number;
}

export interface FlashcardBook {
  id: string;
  title: string;
  slug: string;
  description: string;
  introAudioUrl: string;
  turnCodes: Record<string, string>;
  colorMappings: ColorMapping[];
  enabledColors: string[];
  phase: string;
  fontSize: number;
  isPublic: boolean;
  isPublished: boolean;
  createdAt: string;
}

export interface FlashcardPage {
  id: string;
  flashcardBookId: string;
  pageNumber: number;
  word: string;
}

export interface ColorMapping {
  color_name: string;
  hex_code: string;
  description?: string;
}

export interface PortalSettings {
  id: string;
  founderImageUrl: string;
  founderName: string;
  defaultIntroMessage: string;
  defaultVoiceId: string;
}

export interface Recording {
  id: string;
  storybookId: string;
  parentId: string;
  childName: string;
  audioUrl: string;
  durationSeconds: number;
  recordedAt: string;
  notes: string;
  isFavorite: boolean;
}

export interface UserProfile {
  id: string;
  userId: string;
  email: string;
  name: string;
  isParent: boolean;
  adminLevel: string | null;
  adminIsActive: boolean;
}

export interface TurnColorWord {
  id: string;
  word: string;
  turnCode: string;
  category: string;
  difficulty: string;
  definition: string;
  phase: string;
}

// =============================================================================
// TRANSFORM HELPERS
// =============================================================================

function parseJson<T>(value: Json | null | undefined, fallback: T): T {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as unknown as T;
}

function str(value: string | null | undefined, fallback = ''): string {
  return value ?? fallback;
}

function num(value: number | null | undefined, fallback = 0): number {
  return value ?? fallback;
}

function bool(value: boolean | null | undefined, fallback = false): boolean {
  return value ?? fallback;
}

function arr<T>(value: T[] | null | undefined, fallback: T[] = []): T[] {
  return value ?? fallback;
}

// =============================================================================
// TRANSFORMERS
// =============================================================================

export function transformStorybook(raw: RawStorybook): Storybook {
  return {
    id: raw.id,
    title: raw.title,
    slug: raw.slug,
    description: str(raw.description),
    coverImageUrl: str(raw.cover_image_url),
    introAudioUrl: str(raw.intro_audio_url),
    founderImageUrl: str(raw.founder_image_url),
    turnCodes: parseJson<Record<string, string>>(raw.turn_codes, {}),
    colorMappings: parseJson<ColorMapping[]>(raw.color_mappings, []),
    enabledColors: arr(raw.enabled_colors),
    phase: str(raw.phase, 'phase1'),
    ageRangeMin: num(raw.age_range_min, 3),
    ageRangeMax: num(raw.age_range_max, 8),
    isPublic: bool(raw.is_public),
    isPublished: bool(raw.is_published),
    createdAt: str(raw.created_at),
  };
}

export function transformStorybooks(raw: RawStorybook[]): Storybook[] {
  return raw.map(transformStorybook);
}

export function transformStorybookPage(raw: RawStorybookPage): StorybookPage {
  return {
    id: raw.id,
    storybookId: str(raw.storybook_id),
    pageNumber: raw.page_number,
    text: raw.text,
    imageUrl: str(raw.image_url),
    sceneDescription: str(raw.scene_description),
    audioUrl: str(raw.audio_url),
    audioTimestamps: parseJson<AudioTimestamp[]>(raw.audio_timestamps, []),
  };
}

export function transformStorybookPages(raw: RawStorybookPage[]): StorybookPage[] {
  return raw.map(transformStorybookPage);
}

export function transformFlashcardBook(raw: RawFlashcardBook): FlashcardBook {
  return {
    id: raw.id,
    title: raw.title,
    slug: raw.slug,
    description: str(raw.description),
    introAudioUrl: str(raw.intro_audio_url),
    turnCodes: parseJson<Record<string, string>>(raw.turn_codes, {}),
    colorMappings: parseJson<ColorMapping[]>(raw.color_mappings, []),
    enabledColors: arr(raw.enabled_colors),
    phase: str(raw.phase, 'phase1'),
    fontSize: num(raw.font_size, 72),
    isPublic: bool(raw.is_public),
    isPublished: bool(raw.is_published),
    createdAt: str(raw.created_at),
  };
}

export function transformFlashcardBooks(raw: RawFlashcardBook[]): FlashcardBook[] {
  return raw.map(transformFlashcardBook);
}

export function transformFlashcardPage(raw: RawFlashcardPage): FlashcardPage {
  return {
    id: raw.id,
    flashcardBookId: str(raw.flashcard_book_id),
    pageNumber: raw.page_number,
    word: raw.word,
  };
}

export function transformFlashcardPages(raw: RawFlashcardPage[]): FlashcardPage[] {
  return raw.map(transformFlashcardPage);
}

export function transformPortalSettings(raw: RawPortalSettings): PortalSettings {
  return {
    id: raw.id,
    founderImageUrl: str(raw.founder_image_url),
    founderName: str(raw.founder_name, 'Turn Literacy'),
    defaultIntroMessage: str(raw.default_intro_message),
    defaultVoiceId: str(raw.default_voice_id),
  };
}

export function transformRecording(raw: RawReadingRecording): Recording {
  return {
    id: raw.id,
    storybookId: raw.storybook_id,
    parentId: raw.parent_id,
    childName: str(raw.child_name),
    audioUrl: raw.audio_url,
    durationSeconds: raw.duration_seconds,
    recordedAt: str(raw.recorded_at, raw.created_at ?? new Date().toISOString()),
    notes: str(raw.notes),
    isFavorite: bool(raw.is_favorite),
  };
}

export function transformRecordings(raw: RawReadingRecording[]): Recording[] {
  return raw.map(transformRecording);
}

export function transformProfile(raw: RawProfile): UserProfile {
  return {
    id: raw.id,
    userId: str(raw.user_id),
    email: str(raw.email),
    name: raw.name,
    isParent: bool(raw.is_parent),
    adminLevel: raw.admin_level,
    adminIsActive: bool(raw.admin_is_active, true),
  };
}

export function transformTurnColorWord(raw: RawTurnColorWord): TurnColorWord {
  return {
    id: raw.id,
    word: raw.word,
    turnCode: raw.turn_code,
    category: str(raw.category),
    difficulty: str(raw.difficulty),
    definition: str(raw.definition),
    phase: str(raw.phase, 'phase1'),
  };
}

export function transformTurnColorWords(raw: RawTurnColorWord[]): TurnColorWord[] {
  return raw.map(transformTurnColorWord);
}

export function transformColorMapping(raw: RawTurnColorMapping): ColorMapping {
  return {
    color_name: raw.color_name,
    hex_code: raw.hex_code,
    description: str(raw.description),
  };
}

export function transformColorMappings(raw: RawTurnColorMapping[]): ColorMapping[] {
  return raw.map(transformColorMapping);
}





